import React, { useCallback, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BaseDialog from "../../utils/dialog/BaseDialog";
import UploadDocumentStep from "./UploadDocumentStep";
import ClassifyLoading from "./ClassifyLoading";
import ClassifyProcessGuideTrigger, {
  getGuideFlowIndex,
} from "./ClassifyProcessGuide";
import {
  InvitationAccessDialog,
  isInvitationFeatureEnabled,
  useInvitationAccess,
} from "../../../library/Dialog";

const CLASSIFICATION_INVITATION_ENABLED = isInvitationFeatureEnabled(
  process.env.REACT_APP_GENERAL_INVITATION_SCREEN_ENABLED
);
const CLASSIFICATION_INVITATION_KEY = `${
  process.env.REACT_APP_GENERAL_INVITATION_KEY || ""
}`.trim();
const CLASSIFICATION_INVITATION_STORAGE_KEY = "ai_auto_classify_invitation_key";

const ClassificationEngineDialog = ({
  open,
  onClose,
  ColumnSelectionStep,
  ReviewStep,
  classify,
  columnSelectionProps = {},
  reviewProps = {},
  uploadProps = {},
  loadingProps = {},
  guideProps,
  title = "AI Auto-Classify",
  subtitle = "Extract and classify document data",
  classifyErrorMessage = "Data could not be classified. Please try again.",
  onApply,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [extractionResult, setExtractionResult] = useState(null);
  const [originalRows, setOriginalRows] = useState([]);
  const [classifiedRows, setClassifiedRows] = useState([]);
  const [classifyOptions, setClassifyOptions] = useState(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationComplete, setClassificationComplete] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [classifyError, setClassifyError] = useState("");
  const [uploadResetSignal, setUploadResetSignal] = useState(0);
  const { acceptInvitation, hasAccess } = useInvitationAccess({
    enabled: CLASSIFICATION_INVITATION_ENABLED,
    invitationKey: CLASSIFICATION_INVITATION_KEY,
    storageKey: CLASSIFICATION_INVITATION_STORAGE_KEY,
  });

  const handleExtracted = (result) => {
    setExtractionResult(result);
    if (result) setActiveStep(1);
  };

  const resetState = () => {
    setActiveStep(0);
    setExtractionResult(null);
    setOriginalRows([]);
    setClassifiedRows([]);
    setClassifyOptions(null);
    setIsClassifying(false);
    setClassificationComplete(false);
    setIsApplying(false);
    setClassifyError("");
    setUploadResetSignal((previous) => previous + 1);
  };

  const handleBackToUpload = () => {
    setActiveStep(0);
    setExtractionResult(null);
    setOriginalRows([]);
    setClassifiedRows([]);
    setClassifyOptions(null);
    setClassifyError("");
    setUploadResetSignal((previous) => previous + 1);
  };

  const handleClose = () => {
    if (isClassifying || classificationComplete || isApplying) return;
    resetState();
    onClose?.();
  };

  const handleApplyClassified = async (...args) => {
    if (!onApply) return;
    setIsApplying(true);
    try {
      await onApply(...args);
      onClose?.();
      resetState();
    } finally {
      setIsApplying(false);
    }
  };

  const handleConfirmSnapshot = async (rows, options) => {
    setClassificationComplete(false);
    setClassifyError("");
    setOriginalRows(rows);
    setClassifyOptions(options);
    setIsClassifying(true);
    try {
      // Each caller adapts its API response to { rows, options }.
      const classified = await classify(rows, options);
      setClassifiedRows(classified.rows);
      if (classified.options && Object.keys(classified.options).length) {
        setClassifyOptions((previous) => ({
          ...(previous || {}),
          ...classified.options
        }));
      }
      setActiveStep(2);
      setClassificationComplete(true);
    } catch (error) {
      setClassifyError(error?.message || classifyErrorMessage);
    } finally {
      setIsClassifying(false);
    }
  };

  const handleClassificationComplete = useCallback(() => {
    setClassificationComplete(false);
  }, []);

  const dialogMaxWidth = activeStep >= 1 || isClassifying ? "xl" : "md";
  const useConstrainedContent = activeStep >= 1 && !isClassifying;
  const useCompactLoadingContent = isClassifying;

  if (!hasAccess) {
    return (
      <InvitationAccessDialog
        open={open}
        onClose={handleClose}
        onSubmit={acceptInvitation}
        title='AI Auto-Classify Early Access'
        subtitle='Invitation-only testing is currently in progress.'
        featureName='AI Auto-Classify'
        description='We are inviting a limited group to test AI Auto-Classify and help us refine the experience before wider release.'
      />
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      closeDisabled={isClassifying || classificationComplete || isApplying}
      title={title}
      subtitle={subtitle}
      icon={<AutoAwesomeIcon sx={{ fontSize: 28 }} />}
      headerAdornment={
        guideProps && (
          <ClassifyProcessGuideTrigger
            {...guideProps}
            highlighted={activeStep === 0 && !isClassifying}
            compact
            initialFlowIndex={getGuideFlowIndex({
              activeStep,
              isClassifying,
              classificationComplete,
            })}
          />
        )
      }
      maxWidth={dialogMaxWidth}
      contentSx={
        useConstrainedContent || useCompactLoadingContent
          ? {
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              overflow: useConstrainedContent ? "hidden" : "auto",
              p: { xs: 1.5, sm: 2 },
              position: "relative",
              width: "100%",
            }
          : undefined
      }
      paperSx={{
        ...(useConstrainedContent && {
          display: "flex",
          flexDirection: "column",
          height: { xs: "calc(100vh - 16px)", md: "calc(100vh - 32px)" },
          minHeight: 0,
          overflow: "hidden",
        }),
        ...(!useConstrainedContent && {
          minHeight: useCompactLoadingContent
            ? 0
            : activeStep >= 1
              ? 760
              : 600,
          maxHeight: activeStep >= 1 ? "92vh" : undefined,
        }),
        ...(useCompactLoadingContent && {
          maxHeight: { xs: "calc(100vh - 16px)", md: "calc(100vh - 32px)" },
          minHeight: 0,
          overflow: "hidden",
        }),
      }}
    >
      {isClassifying ? (
        <ClassifyLoading {...loadingProps} rowCount={originalRows.length} />
      ) : (
        <>
          {classifyError && activeStep === 1 && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {classifyError}
            </Alert>
          )}
          {activeStep === 0 && (
            <UploadDocumentStep
              {...uploadProps}
              guideProps={guideProps}
              extractionResult={extractionResult}
              onExtracted={handleExtracted}
              onContinue={() => setActiveStep(1)}
              resetSignal={uploadResetSignal}
            />
          )}
          {activeStep === 1 && (
            <ColumnSelectionStep
              {...columnSelectionProps}
              extractionResult={extractionResult}
              onBack={handleBackToUpload}
              onConfirm={handleConfirmSnapshot}
            />
          )}
          {activeStep === 2 && (
            <ReviewStep
              {...reviewProps}
              classifiedRows={classifiedRows}
              originalRows={originalRows}
              classifyOptions={classifyOptions}
              onRowsChange={setClassifiedRows}
              onBack={() => setActiveStep(1)}
              onApply={onApply ? handleApplyClassified : undefined}
              applying={isApplying}
            />
          )}
          {classificationComplete && (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "background.paper",
                display: "flex",
                inset: 0,
                justifyContent: "center",
                position: "absolute",
                zIndex: 2,
              }}
            >
              <ClassifyLoading
                {...loadingProps}
                complete
                rowCount={originalRows.length}
                onComplete={handleClassificationComplete}
              />
            </Box>
          )}
        </>
      )}
    </BaseDialog>
  );
};

const AIAssistantButton = ({ buttonLabel = "AI Auto-Classify", ...dialogProps }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant='outlined'
        startIcon={<AutoAwesomeIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
      >
        {buttonLabel}
      </Button>
      <ClassificationEngineDialog
        {...dialogProps}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export { AIAssistantButton, ClassificationEngineDialog };
export default AIAssistantButton;
